import { useEffect, useState } from 'react';
import { useAppShell } from '../layout/AppShellContext';
import './GuidePage.css';

// Guide d'utilisation de l'app mobile MyTravo, destiné aux maîtres d'œuvre.
// Les captures d'écran se déposent dans public/guide-captures/<nom>.png : tant qu'un fichier
// manque, un emplacement réservé indique le nom attendu.

const CAPTURES_URL = `${process.env.PUBLIC_URL}/guide-captures`;

const SECTIONS = [
  { id: 'intro', title: 'MyTravo en bref' },
  { id: 'onglets', title: 'Les écrans d’accueil' },
  {
    id: 'creation',
    title: 'Créer un chantier',
    children: [
      { id: 'creation-modes', title: 'Automatisation ou modèle ?' },
      { id: 'creation-auto', title: 'L’automatisation pas à pas' },
      { id: 'creation-ia', title: 'Ce que fait l’IA' },
      { id: 'creation-conseils', title: 'Bien préparer ses documents' },
      { id: 'creation-maj', title: 'Mettre à jour un chantier' },
      { id: 'creation-modele', title: 'Créer depuis un modèle' },
    ],
  },
  {
    id: 'chantier',
    title: 'L’écran chantier',
    children: [
      { id: 'chantier-avancement', title: 'Avancement et phase' },
      { id: 'chantier-equipe', title: 'Équipe et contacts' },
      { id: 'chantier-lots', title: 'Lots et tâches' },
      { id: 'chantier-suivi', title: 'Suivi, rapports, fichiers' },
    ],
  },
  {
    id: 'cr',
    title: 'MyCR : de la réunion au CR',
    children: [
      { id: 'cr-preparer', title: 'Préparer la réunion' },
      { id: 'cr-enregistrer', title: 'Enregistrer' },
      { id: 'cr-ia', title: 'Ce que l’IA comprend' },
      { id: 'cr-valider', title: 'Relire et valider' },
      { id: 'cr-apres', title: 'Après la validation' },
      { id: 'cr-ri', title: 'Réunion intermédiaire' },
    ],
  },
  { id: 'faq', title: 'Questions fréquentes' },
];

const ALL_IDS = SECTIONS.flatMap((section) => [section.id, ...(section.children || []).map((child) => child.id)]);

function Screenshot({ name, caption }) {
  const [missing, setMissing] = useState(false);
  return (
    <figure className="Guide-shot">
      {missing ? (
        <div className="Guide-shotPlaceholder">
          <span className="Guide-shotPlaceholderTitle">Capture à ajouter</span>
          <code>public/guide-captures/{name}.png</code>
        </div>
      ) : (
        <img src={`${CAPTURES_URL}/${name}.png`} alt={caption} loading="lazy" onError={() => setMissing(true)} />
      )}
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function Shots({ children }) {
  return <div className="Guide-shots">{children}</div>;
}

function Callout({ tone = 'info', title, children }) {
  return (
    <aside className={`Guide-callout Guide-callout--${tone}`}>
      {title && <strong className="Guide-calloutTitle">{title}</strong>}
      <div>{children}</div>
    </aside>
  );
}

function Steps({ children }) {
  return <ol className="Guide-steps">{children}</ol>;
}

// Enchaînement d'étapes en ligne (passe à la ligne sur petit écran).
function Flow({ items }) {
  return (
    <ol className="Guide-flow">
      {items.map((item, index) => (
        <li key={item.title} className="Guide-flowItem">
          <span className="Guide-flowIndex">{index + 1}</span>
          <strong>{item.title}</strong>
          <span>{item.text}</span>
        </li>
      ))}
    </ol>
  );
}

function Section({ id, title, kicker, children }) {
  return (
    <section id={id} className="Guide-section">
      {kicker && <p className="Guide-kicker">{kicker}</p>}
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function SubSection({ id, title, children }) {
  return (
    <div id={id} className="Guide-subsection">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function useActiveSection() {
  const [activeId, setActiveId] = useState(ALL_IDS[0]);

  useEffect(() => {
    const elements = ALL_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -65% 0px' }
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return activeId;
}

function GuidePage() {
  const { setCrumb } = useAppShell();
  const activeId = useActiveSection();

  useEffect(() => {
    setCrumb("Guide de l'app mobile");
    return () => setCrumb(null);
  }, [setCrumb]);

  return (
    <div className="Guide">
      <nav className="Guide-toc" aria-label="Sommaire du guide">
        <p className="Guide-tocTitle">Sommaire</p>
        <ol>
          {SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`} className={activeId === section.id ? 'is-active' : undefined}>
                {section.title}
              </a>
              {section.children && (
                <ol>
                  {section.children.map((child) => (
                    <li key={child.id}>
                      <a href={`#${child.id}`} className={activeId === child.id ? 'is-active' : undefined}>
                        {child.title}
                      </a>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          ))}
        </ol>
      </nav>

      <article className="Guide-content">
        <header className="Guide-hero">
          <p className="Guide-kicker">Guide d’utilisation</p>
          <h1>
            L’app mobile My<span>Travo</span>
          </h1>
          <p className="Guide-lead">
            MyTravo prépare votre chantier à partir de vos documents de marché, rédige vos comptes-rendus pendant que
            vous menez la réunion, et tient le planning à jour à chaque visite. Ce guide vous montre comment en tirer
            parti, sans connaissance informatique particulière.
          </p>
        </header>

        {/* ------------------------------------------------------------------ */}
        <Section id="intro" kicker="Pour commencer" title="MyTravo en bref">
          <div className="Guide-cards">
            <div className="Guide-card">
              <strong>Un chantier prêt en quelques minutes</strong>
              <p>
                Importez le DPGF, le CCTP ou le planning : l’IA crée les lots, les tâches, les dates et les contacts des
                entreprises.
              </p>
            </div>
            <div className="Guide-card">
              <strong>Des CR rédigés pendant la réunion</strong>
              <p>
                Posez votre téléphone sur la table : MyTravo écoute la réunion et vous propose un compte-rendu structuré à
                relire.
              </p>
            </div>
            <div className="Guide-card">
              <strong>Un planning qui se met à jour seul</strong>
              <p>
                À la validation du CR, les tâches terminées sont cochées, les actions deviennent des tâches et les dates
                des lots sont ajustées.
              </p>
            </div>
          </div>

          <h3>Le cycle d’un chantier dans MyTravo</h3>
          <Flow
            items={[
              { title: 'Documents', text: 'Vous importez les pièces du marché.' },
              { title: 'Chantier structuré', text: 'Lots, tâches, dates et contacts sont créés.' },
              { title: 'Réunion', text: 'MyCR enregistre les échanges.' },
              { title: 'Brouillon de CR', text: 'L’IA rédige, vous relisez et corrigez.' },
              { title: 'Validation', text: 'Le CR part par email, le planning est mis à jour.' },
            ]}
          />
          <p className="Guide-muted">
            À la réunion suivante, MyTravo vous rappelle les actions encore ouvertes et les points bloquants du dernier
            CR : la boucle recommence.
          </p>
        </Section>

        {/* ------------------------------------------------------------------ */}
        <Section id="onglets" kicker="Navigation" title="Les écrans d’accueil">
          <p>
            La barre du bas donne accès aux cinq pages principales. Vous pouvez aussi passer de l’une à l’autre en
            glissant le doigt horizontalement.
          </p>
          <div className="Guide-table" role="table">
            <div role="row">
              <strong role="cell">Accueil</strong>
              <span role="cell">Calendrier des événements à venir, météo de vos chantiers et chantiers récents.</span>
            </div>
            <div role="row">
              <strong role="cell">Chantiers</strong>
              <span role="cell">
                La liste de vos chantiers. Le bouton <em>+</em> permet de créer un projet ou de rejoindre un chantier
                existant (sur invitation ou par QR code).
              </span>
            </div>
            <div role="row">
              <strong role="cell">Photo</strong>
              <span role="cell">
                Prenez une photo sur site, annotez-la si besoin, puis rattachez-la à une tâche d’un chantier avec un
                commentaire.
              </span>
            </div>
            <div role="row">
              <strong role="cell">Messages</strong>
              <span role="cell">Chaque chantier a sa discussion ; vos contacts y sont aussi regroupés.</span>
            </div>
            <div role="row">
              <strong role="cell">Profil</strong>
              <span role="cell">Vos informations, vos métiers, votre abonnement et la déconnexion.</span>
            </div>
          </div>
          <Shots>
            <Screenshot name="onglet-accueil" caption="L’accueil et la barre de navigation" />
            <Screenshot name="onglet-chantiers" caption="La liste des chantiers" />
            <Screenshot name="onglet-photo" caption="La capture photo" />
          </Shots>
        </Section>

        {/* ------------------------------------------------------------------ */}
        <Section id="creation" kicker="Étape 1" title="Créer un chantier">
          <p>
            Depuis l’onglet <strong>Chantiers</strong>, appuyez sur <em>+</em> puis sur <strong>Créer un projet</strong>.
            Donnez un nom au projet (par exemple « Rénovation médiathèque ») et ajoutez une photo si vous le souhaitez.
          </p>

          <SubSection id="creation-modes" title="Automatisation ou modèle ?">
            <p>MyTravo vous demande ensuite comment créer le projet :</p>
            <div className="Guide-compare">
              <div className="Guide-compareItem Guide-compareItem--accent">
                <span className="Guide-badge">Recommandé</span>
                <strong>Automatisation</strong>
                <p>
                  Vous avez les pièces du marché (DPGF, CCTP, CCAP, planning…). L’IA les lit et construit le chantier
                  pour vous.
                </p>
                <p className="Guide-muted">Idéal pour un marché alloti avec plusieurs entreprises.</p>
              </div>
              <div className="Guide-compareItem">
                <strong>Modèle de projet</strong>
                <p>
                  Vous n’avez pas de documents, ou il s’agit d’un petit chantier. Vous choisissez un type de projet et
                  MyTravo propose une liste de tâches types.
                </p>
                <p className="Guide-muted">Idéal pour démarrer vite un chantier simple.</p>
              </div>
            </div>
            <Shots>
              <Screenshot name="creation-choix" caption="Créer un projet ou rejoindre un chantier" />
              <Screenshot name="creation-mode" caption="Nom du projet et choix du mode" />
            </Shots>
          </SubSection>

          <SubSection id="creation-auto" title="L’automatisation pas à pas">
            <Steps>
              <li>
                <strong>Importez vos documents.</strong> L’écran propose trois emplacements :
                <ul>
                  <li>
                    <strong>Marché de travaux</strong> : CCAP, CCTP, DPGF ou décomposition des lots. PDF ou Excel, un ou
                    plusieurs fichiers.
                  </li>
                  <li>
                    <strong>Documents complémentaires</strong> : devis, contrats, notes… (PDF).
                  </li>
                  <li>
                    <strong>Planning prévisionnel</strong> : planning général ou Gantt par lot (PDF ou Excel).
                  </li>
                </ul>
                Chaque fichier peut peser jusqu’à 50 Mo. Aucun document n’est obligatoire : sans document, le bouton
                devient <em>Passer</em> et le chantier est créé vide.
              </li>
              <li>
                <strong>Vérifiez le récapitulatif</strong> (nom, photo, documents importés), puis appuyez sur{' '}
                <strong>Créer le projet</strong>.
              </li>
              <li>
                <strong>Patientez pendant l’analyse.</strong> Une barre de progression affiche trois étapes :{' '}
                <em>Envoi</em>, <em>Analyse</em>, <em>Création des lots</em>. Comptez jusqu’à 3 minutes pour des
                documents volumineux.
              </li>
              <li>
                <strong>Contrôlez les lots détectés.</strong> Pour chaque lot, vous voyez le nom, la description, le
                montant prévisionnel, la durée, la date de début et les métiers. Vous pouvez tout modifier, supprimer un
                lot, en ajouter un, ou tout déplier pour une vue complète.
              </li>
              <li>
                Appuyez sur <strong>Valider et continuer</strong> : votre chantier est prêt, avec ses lots, ses tâches,
                ses dates et ses contacts.
              </li>
            </Steps>
            <Shots>
              <Screenshot name="creation-documents" caption="L’import des documents" />
              <Screenshot name="creation-recap" caption="Le récapitulatif avant création" />
              <Screenshot name="creation-analyse" caption="L’analyse en cours" />
              <Screenshot name="creation-lots" caption="Les lots détectés, à vérifier" />
            </Shots>
            <Callout tone="warn" title="Si l’analyse échoue">
              Un message <em>L’analyse a échoué</em> s’affiche avec deux choix : <strong>Réessayer</strong>, ou{' '}
              <strong>Passer, créer sans lots</strong> pour garder le chantier et ajouter les lots à la main. Les
              documents restent rattachés au chantier dans tous les cas.
            </Callout>
            <Callout tone="tip" title="Vos fichiers sont sur votre ordinateur ?">
              Le portail web propose la même création par automatisation : depuis le tableau de bord, cliquez sur{' '}
              <strong>Créer un projet</strong> et déposez vos fichiers directement depuis votre PC. Le chantier apparaît
              ensuite dans l’app mobile.
            </Callout>
          </SubSection>

          <SubSection id="creation-ia" title="Ce que fait l’IA en coulisses">
            <p>
              L’IA ne se contente pas de recopier un document : elle <strong>lit toutes vos pièces ensemble</strong> et les
              recoupe, comme le ferait un conducteur de travaux qui prépare son dossier.
            </p>

            <h4>1. Elle croise les documents selon leur valeur contractuelle</h4>
            <ul className="Guide-list">
              <li>
                Le <strong>DPGF</strong> (ou le marché au format Excel) fait foi pour la liste des lots, leur numérotation
                et les montants.
              </li>
              <li>
                Le <strong>règlement de consultation</strong>, s’il est fourni, fait foi pour l’intitulé officiel des lots.
              </li>
              <li>
                Le <strong>CCTP</strong> sert à enrichir la description des lots et à identifier les métiers. Il ne
                contredit jamais un numéro ou un montant du DPGF.
              </li>
              <li>Un lot présent dans plusieurs documents n’est créé qu’une fois : les informations sont fusionnées.</li>
            </ul>

            <h4>2. Elle extrait, pour chaque lot</h4>
            <div className="Guide-table" role="table">
              <div role="row">
                <strong role="cell">Numéro et intitulé</strong>
                <span role="cell">Tels qu’écrits dans le document de référence.</span>
              </div>
              <div role="row">
                <strong role="cell">Description</strong>
                <span role="cell">Un résumé du contenu du lot, enrichi par toutes les pièces.</span>
              </div>
              <div role="row">
                <strong role="cell">Montant prévisionnel</strong>
                <span role="cell">S’il figure dans une des pièces.</span>
              </div>
              <div role="row">
                <strong role="cell">Métiers</strong>
                <span role="cell">Choisis dans le référentiel des métiers de MyTravo (plombier, électricien…).</span>
              </div>
              <div role="row">
                <strong role="cell">Date de début et durée</strong>
                <span role="cell">
                  La date de démarrage réel des travaux (planning, ordre de service), jamais une date administrative
                  comme la remise des offres ou la notification.
                </span>
              </div>
              <div role="row">
                <strong role="cell">Entreprise et email</strong>
                <span role="cell">
                  L’entreprise titulaire du lot et son email, uniquement s’ils sont écrits noir sur blanc. Le maître
                  d’ouvrage ou le maître d’œuvre ne sont jamais pris pour une entreprise.
                </span>
              </div>
              <div role="row">
                <strong role="cell">Tâches</strong>
                <span role="cell">
                  Regroupées par familles d’ouvrages, avec leurs dates uniquement si elles sont explicitement indiquées.
                </span>
              </div>
            </div>

            <h4>3. MyTravo complète ensuite le chantier</h4>
            <ul className="Guide-list">
              <li>
                <strong>Tâches types</strong> : pour chaque métier du lot, MyTravo ajoute les tâches habituelles de sa
                bibliothèque, sans créer de doublon avec celles trouvées dans vos documents.
              </li>
              <li>
                <strong>Enchaînement des tâches</strong> : les dépendances entre tâches sont calculées pour construire le
                planning.
              </li>
              <li>
                <strong>Dates du chantier</strong> : le début correspond au lot qui démarre le plus tôt, la fin au lot qui
                se termine le plus tard.
              </li>
              <li>
                <strong>Contacts</strong> : chaque entreprise trouvée devient un contact rattaché à son lot. Elle recevra
                les comptes-rendus par email, sans avoir à installer l’application.
              </li>
            </ul>

            <Callout tone="info" title="L’IA n’invente rien">
              Quand une information n’est pas dans vos documents, le champ reste vide plutôt que d’être deviné. En
              particulier, une adresse email n’est jamais reconstituée à partir d’un nom ou d’un site web. C’est pour cela
              que l’écran de vérification des lots existe : vous complétez ce qui manque avant de valider.
            </Callout>
          </SubSection>

          <SubSection id="creation-conseils" title="Bien préparer ses documents">
            <ul className="Guide-list">
              <li>
                <strong>Fournissez le DPGF en priorité</strong>, idéalement au format Excel : c’est lui qui donne la liste
                exacte des lots et des montants.
              </li>
              <li>
                <strong>Ajoutez le planning</strong> si vous l’avez : sans lui, les lots sont créés sans date et la
                timeline reste vide.
              </li>
              <li>
                <strong>Ajoutez le CCTP</strong> pour des descriptions plus précises et un meilleur choix des métiers.
              </li>
              <li>
                <strong>Les marchés signés sont les plus utiles</strong> : ils mentionnent le nom des entreprises
                titulaires et parfois leurs emails, qui deviennent vos contacts.
              </li>
              <li>Respectez la limite de 50 Mo par fichier ; découpez les dossiers trop lourds.</li>
            </ul>
          </SubSection>

          <SubSection id="creation-maj" title="Mettre à jour un chantier existant">
            <p>
              Un avenant, un nouveau lot attribué, un planning recalé ? Inutile de tout ressaisir. Sur l’écran du
              chantier, appuyez sur <em>+</em> à côté de <strong>Lots du chantier</strong>, puis sur{' '}
              <strong>Mettre à jour le chantier</strong> (via l’automatisation).
            </p>
            <Steps>
              <li>Importez les nouveaux documents (marché, avenant, planning, documents complémentaires).</li>
              <li>
                L’IA compare ces documents aux lots existants : elle <strong>ajoute les nouveaux lots</strong>,{' '}
                <strong>complète les informations manquantes</strong> (montant, dates, durée, métiers, contact) et ajoute
                les tâches décrites.
              </li>
              <li>
                L’écran <strong>Mises à jour détectées</strong> indique pour chaque lot ce qui a changé (« Complété :
                montant, date de début », « +3 tâches »…). Vérifiez, puis appuyez sur{' '}
                <strong>Valider les mises à jour</strong>.
              </li>
            </Steps>
            <Callout tone="info" title="Aucune donnée existante n’est écrasée">
              La mise à jour ne fait que compléter : une information que vous avez déjà saisie n’est jamais remplacée.
            </Callout>
            <Shots>
              <Screenshot name="maj-documents" caption="Import des documents de mise à jour" />
              <Screenshot name="maj-recap" caption="Les mises à jour détectées" />
            </Shots>
          </SubSection>

          <SubSection id="creation-modele" title="Créer depuis un modèle de projet">
            <Steps>
              <li>Choisissez une catégorie puis un type de projet (par exemple une rénovation de salle de bain).</li>
              <li>
                MyTravo propose des <strong>tâches suggérées, classées par métier</strong>. Cochez celles qui vous
                concernent (ou utilisez <em>Tout sélectionner</em>).
              </li>
              <li>
                Appuyez sur <strong>Créer le projet</strong>. Vous pourrez ajouter des lots et des tâches plus tard.
              </li>
            </Steps>
            <Shots>
              <Screenshot name="modele-type" caption="Choix du type de projet" />
              <Screenshot name="modele-taches" caption="Sélection des tâches suggérées" />
            </Shots>
          </SubSection>
        </Section>

        {/* ------------------------------------------------------------------ */}
        <Section id="chantier" kicker="Étape 2" title="L’écran chantier, le cœur de l’app">
          <p>
            Tout ce qui concerne un chantier se trouve sur une seule page, que l’on parcourt de haut en bas : en-tête,
            avancement, dates, équipe, lots, comptes-rendus et documents.
          </p>
          <Shots>
            <Screenshot name="chantier-entete" caption="En-tête, avancement et dates" />
            <Screenshot name="chantier-vue" caption="Équipe et lots du chantier" />
          </Shots>
          <p>
            L’<strong>en-tête</strong> affiche la photo, le type de projet et l’adresse. Le crayon en haut à droite
            permet de modifier les informations du chantier.
          </p>

          <SubSection id="chantier-avancement" title="Avancement et phase">
            <p>
              La carte <strong>Avancement</strong> indique la phase du projet et le nombre de tâches terminées. Un appui
              ouvre le détail : la liste des tâches et la <strong>timeline des lots</strong>, filtrable (en cours, à venir,
              terminés) et zoomable.
            </p>
            <div className="Guide-phases">
              <span style={{ '--phase': '#9CA3AF' }}>Prospect</span>
              <span style={{ '--phase': '#3B82F6' }}>Découverte</span>
              <span style={{ '--phase': '#8B5CF6' }}>Proposition</span>
              <span style={{ '--phase': '#F6A500' }}>Projet</span>
              <span style={{ '--phase': '#10B981' }}>Clôture</span>
            </div>
            <p>Des bandeaux de couleur apparaissent sous l’avancement quand une action est attendue :</p>
            <ul className="Guide-list">
              <li>
                <strong>Informations manquantes</strong> (orange) : il manque la date de fin ou l’adresse. Un appui ouvre
                la modification.
              </li>
              <li>
                <strong>Toutes les tâches sont terminées</strong> (vert) : un appui permet de marquer le chantier comme
                terminé.
              </li>
            </ul>
            <Screenshot name="chantier-timeline" caption="La timeline des lots" />
          </SubSection>

          <SubSection id="chantier-equipe" title="Équipe et contacts">
            <p>La section Équipe regroupe deux types d’intervenants :</p>
            <div className="Guide-compare">
              <div className="Guide-compareItem">
                <strong>Membres</strong>
                <p>Ils ont un compte MyTravo et voient le chantier dans leur application.</p>
              </div>
              <div className="Guide-compareItem">
                <strong>Contacts par email</strong>
                <p>
                  Ils n’ont pas besoin de s’inscrire : ils reçoivent les comptes-rendus par email pour les lots auxquels
                  ils sont rattachés.
                </p>
              </div>
            </div>
            <p>
              Pour ajouter un contact, appuyez sur <strong>Ajouter</strong> puis <strong>Ajouter par email</strong>.
              Renseignez le nom (entreprise ou interlocuteur), l’email et les <strong>lots concernés</strong>.
            </p>
            <Callout tone="warn" title="Pas d’email, pas de CR">
              Un contact sans email porte la mention « Pas d’email · ne recevra pas le CR ». Pensez à compléter les
              emails que l’IA n’a pas trouvés dans les documents.
            </Callout>
            <Shots>
              <Screenshot name="chantier-equipe" caption="La section Équipe" />
              <Screenshot name="chantier-contact" caption="Ajout d’un contact par email" />
            </Shots>
          </SubSection>

          <SubSection id="chantier-lots" title="Lots et tâches">
            <p>
              Les lots défilent en carrousel : glissez horizontalement pour passer de l’un à l’autre. Chaque carte
              affiche le statut du lot, ses métiers, sa période planifiée, l’entreprise ou le contact associé, et ses
              tâches.
            </p>
            <ul className="Guide-list">
              <li>
                <strong>Appuyez sur une tâche</strong> pour voir son détail, ses photos, la modifier ou la terminer.
              </li>
              <li>
                <strong>Appuyez longuement sur un lot</strong> pour réorganiser l’ordre des lots.
              </li>
              <li>
                Le bouton <em>+</em> propose : <strong>Mettre à jour le chantier</strong> (automatisation),{' '}
                <strong>Nouvelle tâche</strong> ou <strong>Nouveau lot</strong>.
              </li>
            </ul>
            <p className="Guide-muted">Les montants des lots ne sont visibles que pour les rôles qui y ont accès.</p>
            <Shots>
              <Screenshot name="chantier-lot" caption="Une carte de lot" />
              <Screenshot name="chantier-ajout" caption="Le menu d’ajout" />
            </Shots>
          </SubSection>

          <SubSection id="chantier-suivi" title="Suivi, rapports et fichiers">
            <ul className="Guide-list">
              <li>
                <strong>MyCR</strong> : vos comptes-rendus de réunion (voir la partie suivante). Un badge{' '}
                <em>À envoyer</em> signale une réunion enregistrée qui n’a pas encore été transmise, par exemple faute de
                réseau.
              </li>
              <li>
                <strong>MyAssistant</strong> : posez vos questions sur le chantier en langage courant. « Que dois-je
                évoquer à la prochaine réunion ? », « Quelles actions sont encore ouvertes, et par qui ? », « Quelles
                tâches sont en retard ? ». L’assistant répond à partir des données du chantier et cite les CR utilisés.
              </li>
              <li>
                <strong>Fichiers</strong> : tous les documents du chantier, dont les pièces du marché importées à la
                création.
              </li>
            </ul>
            <Shots>
              <Screenshot name="chantier-suivi" caption="Suivi & Rapports" />
              <Screenshot name="assistant" caption="MyAssistant" />
            </Shots>
          </SubSection>
        </Section>

        {/* ------------------------------------------------------------------ */}
        <Section id="cr" kicker="Étape 3" title="MyCR : de la réunion au compte-rendu">
          <p>
            MyCR transforme une réunion de chantier enregistrée en compte-rendu structuré, puis applique les décisions
            au planning. Votre rôle : mener la réunion normalement, puis relire.
          </p>
          <Flow
            items={[
              { title: 'Préparer', text: 'Type de réunion et lots concernés.' },
              { title: 'Enregistrer', text: 'Le téléphone écoute la réunion.' },
              { title: 'Traitement', text: 'Transcription puis rédaction du brouillon.' },
              { title: 'Relire', text: 'Vous corrigez et complétez.' },
              { title: 'Envoyer', text: 'PDF par email et chantier mis à jour.' },
            ]}
          />

          <SubSection id="cr-preparer" title="Préparer la réunion">
            <Steps>
              <li>
                Sur l’écran du chantier, appuyez sur <strong>MyCR</strong>, puis sur <strong>Nouvelle réunion</strong>.
              </li>
              <li>
                Choisissez le type de réunion :
                <ul>
                  <li>
                    <strong>Réunion de chantier</strong> : la réunion officielle, avec un compte-rendu complet adressé à
                    chaque corps de métier.
                  </li>
                  <li>
                    <strong>Réunion intermédiaire</strong> : un point d’étape, dont le résumé des éléments importants est
                    envoyé à tous les participants.
                  </li>
                </ul>
              </li>
              <li>
                Sélectionnez les <strong>lots concernés</strong> par la réunion. L’IA s’en servira comme sujet
                principal, sans ignorer un autre lot s’il est évoqué.
              </li>
              <li>
                Consultez le <strong>Pense-bête</strong> : il reprend les actions non terminées et les points bloquants
                du dernier compte-rendu, ainsi que la date de réunion prévue. Cochez les points au fil de la réunion.
              </li>
            </Steps>
            <Shots>
              <Screenshot name="cr-type" caption="Choix du type de réunion" />
              <Screenshot name="cr-lots" caption="Sélection des lots concernés" />
              <Screenshot name="cr-pense-bete" caption="Le pense-bête" />
            </Shots>
          </SubSection>

          <SubSection id="cr-enregistrer" title="Enregistrer la réunion">
            <Steps>
              <li>
                Appuyez sur <strong>Démarrer la réunion</strong> et posez le téléphone au centre de la table.
                L’enregistrement continue si l’écran se verrouille.
              </li>
              <li>
                Utilisez <strong>Pause</strong> et <strong>Reprendre</strong> pour les apartés qui n’ont pas leur place
                dans le CR.
              </li>
              <li>
                Appuyez sur <strong>Terminer</strong>, puis sur <strong>Lancer le traitement</strong>. Le bouton{' '}
                <em>Recommencer</em> efface l’enregistrement en cours.
              </li>
              <li>
                Le traitement passe par trois étapes : envoi de l’audio, <em>transcription</em>, puis{' '}
                <em>génération du compte-rendu</em>. Vous pouvez fermer la fenêtre : le brouillon apparaîtra dans la
                liste des comptes-rendus une fois prêt.
              </li>
            </Steps>
            <Callout tone="info" title="Bon à savoir">
              Une réunion dure au maximum 2 heures ; MyTravo vous prévient 5 minutes avant l’arrêt automatique. Une seule
              réunion peut être enregistrée à la fois, tous chantiers confondus.
            </Callout>
            <Shots>
              <Screenshot name="cr-enregistrement" caption="Réunion en cours" />
              <Screenshot name="cr-traitement" caption="Transcription et génération" />
            </Shots>
          </SubSection>

          <SubSection id="cr-ia" title="Ce que l’IA comprend de la réunion">
            <p>
              L’IA connaît votre chantier : la liste des lots, leurs tâches, leur planning et les intervenants. Elle peut
              donc relier ce qui est dit aux bons éléments, même si un mot est mal reconnu (« grosse œuvre » est bien
              rattaché au lot Gros œuvre). Elle classe les échanges en catégories :
            </p>
            <div className="Guide-table" role="table">
              <div role="row">
                <strong role="cell">Travaux réalisés</strong>
                <span role="cell">
                  Distingue ce qui est <em>terminé</em> de ce qui est seulement <em>en cours</em>. Un travail terminé et
                  relié à une tâche passera cette tâche au statut Terminé.
                </span>
              </div>
              <div role="row">
                <strong role="cell">Retards</strong>
                <span role="cell">Uniquement les retards et travaux non réalisés dont on a parlé.</span>
              </div>
              <div role="row">
                <strong role="cell">Décisions</strong>
                <span role="cell">Arbitrages, validations et changements actés, avec le lot et le responsable.</span>
              </div>
              <div role="row">
                <strong role="cell">Actions à réaliser</strong>
                <span role="cell">
                  Toute demande ou engagement, avec un responsable et une échéance. Chaque action deviendra une tâche du
                  chantier.
                </span>
              </div>
              <div role="row">
                <strong role="cell">Points bloquants</strong>
                <span role="cell">Ce qui empêche d’avancer ; ils seront rappelés dans le pense-bête suivant.</span>
              </div>
              <div role="row">
                <strong role="cell">Coordination inter-lots</strong>
                <span role="cell">Les points qui concernent plusieurs entreprises.</span>
              </div>
              <div role="row">
                <strong role="cell">Planning</strong>
                <span role="cell">Les nouvelles dates de début et de fin évoquées pour les lots et les tâches.</span>
              </div>
            </div>

            <h4>Exemples</h4>
            <div className="Guide-examples">
              <div>
                <q>Jean, tu peux m’envoyer le plan de plomberie avant vendredi ?</q>
                <span>
                  Action « Envoyer le plan de plomberie », responsable Jean, échéance au vendredi (date calculée), lot
                  Plomberie.
                </span>
              </div>
              <div>
                <q>L’étanchéité de la toiture est terminée.</q>
                <span>Travail réalisé ; la tâche « Étanchéité toitures-terrasses » passera en Terminé.</span>
              </div>
              <div>
                <q>Le curage a bien commencé.</q>
                <span>Travail en cours : la tâche n’est pas marquée comme terminée.</span>
              </div>
              <div>
                <q>Le carreleur démarre lundi.</q>
                <span>Nouvelle date de début pour le lot Carrelage.</span>
              </div>
            </div>

            <Callout tone="tip" title="Pour un CR le plus juste possible">
              Nommez le lot ou l’entreprise, la personne responsable et l’échéance : « Pour le lot 3, l’électricien doit
              finir les saignées avant le 15 ». Dites clairement « terminé » quand un ouvrage est achevé. Les dates
              relatives (« vendredi », « dans 15 jours ») sont converties en dates précises.
            </Callout>
            <Callout tone="info" title="Rien n’est inventé">
              Si une catégorie n’a pas été abordée, elle reste vide et l’app affiche « Aucun élément détecté ». L’IA
              n’écrit jamais « aucune décision n’a été prise » à votre place.
            </Callout>
          </SubSection>

          <SubSection id="cr-valider" title="Relire et valider le compte-rendu">
            <p>
              Ouvrez le brouillon depuis la liste des comptes-rendus. Un bandeau résume ce qui a été détecté (« CR généré
              — 12 éléments détectés »). La validation se fait en quatre temps :
            </p>
            <Steps>
              <li>
                <strong>Le contenu.</strong> Vérifiez les participants (P : présent, C : convoqué, E : excusé, A : absent,
                I : invité) et chaque section. Modifiez une ligne, supprimez-la, ou utilisez <em>+ Ajouter</em>. Indiquez
                la date et l’heure du prochain CR. Si des réunions intermédiaires ont eu lieu depuis le dernier CR, vous
                pouvez les intégrer.
              </li>
              <li>
                <strong>Les mises à jour du planning.</strong> En appuyant sur <em>Suivant</em>, MyTravo analyse les
                décisions et en déduit les changements de planning. La section{' '}
                <strong>Mises à jour de la timeline</strong> montre, pour chaque lot concerné, la période{' '}
                <em>avant</em> et <em>après</em>. Vérifiez puis appuyez de nouveau sur <em>Suivant</em>.
              </li>
              <li>
                <strong>Les photos et documents.</strong> Sélectionnez les photos du chantier à insérer dans le CR, puis
                les documents PDF à joindre à l’email (plans, fiches techniques…).
              </li>
              <li>
                <strong>Les destinataires.</strong> La liste affiche les membres et les contacts qui recevront le CR, et
                signale ceux qui n’ont pas d’email. Cochez la certification de relecture et appuyez sur{' '}
                <strong>Valider et envoyer</strong>.
              </li>
            </Steps>
            <Shots>
              <Screenshot name="cr-brouillon" caption="Le brouillon à relire" />
              <Screenshot name="cr-timeline" caption="Les mises à jour de la timeline" />
              <Screenshot name="cr-photos" caption="Choix des photos" />
              <Screenshot name="cr-destinataires" caption="Les destinataires" />
            </Shots>
            <Callout tone="warn" title="Vous restez responsable du contenu">
              Le brouillon est une proposition. Relisez-le toujours : c’est votre validation qui déclenche l’envoi et la
              mise à jour du chantier. Un brouillon peut aussi être fermé sans confirmer, ou supprimé.
            </Callout>
          </SubSection>

          <SubSection id="cr-apres" title="Ce qui se passe après la validation">
            <div className="Guide-cards">
              <div className="Guide-card">
                <strong>Le CR est envoyé</strong>
                <p>
                  Chaque destinataire reçoit un email avec le compte-rendu en PDF et les documents joints. Les plus gros
                  fichiers sont envoyés sous forme de lien.
                </p>
              </div>
              <div className="Guide-card">
                <strong>Les tâches sont mises à jour</strong>
                <p>
                  Les travaux terminés passent en Terminé, les travaux commencés en En cours, et les nouvelles dates sont
                  appliquées.
                </p>
              </div>
              <div className="Guide-card">
                <strong>Les actions deviennent des tâches</strong>
                <p>Chaque action du CR est ajoutée au chantier, rattachée à son lot, avec son échéance.</p>
              </div>
              <div className="Guide-card">
                <strong>Le planning des lots bouge</strong>
                <p>
                  Les dates et durées des lots sont ajustées, un lot peut être marqué terminé, et la prochaine réunion
                  est ajoutée au calendrier.
                </p>
              </div>
            </div>
            <p>
              Tous vos comptes-rendus restent accessibles dans <strong>MyCR</strong>, avec leur statut :{' '}
              <em>Brouillon</em> ou <em>Confirmé</em> pour les réunions de chantier, <em>Envoyée</em> ou{' '}
              <em>Intégrée à un compte-rendu</em> pour les réunions intermédiaires.
            </p>
            <Screenshot name="cr-liste" caption="La liste des comptes-rendus" />
          </SubSection>

          <SubSection id="cr-ri" title="La réunion intermédiaire">
            <p>
              Pour un point rapide entre deux réunions officielles, choisissez <strong>Réunion intermédiaire</strong>.
              L’enregistrement fonctionne de la même façon, mais le résultat est plus léger :
            </p>
            <ul className="Guide-list">
              <li>un sujet et un résumé des points importants, envoyés tels quels à tous les participants ;</li>
              <li>les nouvelles tâches détectées, qui seront créées à la validation ;</li>
              <li>les tâches terminées et les mises à jour du planning.</li>
            </ul>
            <p>
              À la réunion de chantier suivante, ces réunions intermédiaires peuvent être intégrées au compte-rendu
              officiel.
            </p>
            <Screenshot name="ri-brouillon" caption="Le brouillon d’une réunion intermédiaire" />
          </SubSection>
        </Section>

        {/* ------------------------------------------------------------------ */}
        <Section id="faq" kicker="Aide" title="Questions fréquentes">
          <dl className="Guide-faq">
            <dt>L’IA peut-elle se tromper ?</dt>
            <dd>
              Oui, comme toute lecture automatique. C’est pourquoi chaque résultat passe par un écran de vérification :
              les lots détectés à la création, et le brouillon de CR après une réunion. Rien n’est appliqué sans votre
              validation.
            </dd>

            <dt>Les entreprises doivent-elles installer MyTravo ?</dt>
            <dd>
              Non. Ajoutées comme contacts par email, elles reçoivent les comptes-rendus des lots qui les concernent
              sans créer de compte.
            </dd>

            <dt>Combien de temps prennent les traitements ?</dt>
            <dd>
              L’analyse des documents prend jusqu’à 3 minutes. Pour un CR, comptez quelques minutes selon la durée de la
              réunion ; vous pouvez fermer l’écran pendant ce temps.
            </dd>

            <dt>Je n’ai pas de réseau sur le chantier.</dt>
            <dd>
              Enregistrez quand même la réunion : elle est conservée sur le téléphone et MyCR affiche le badge{' '}
              <em>À envoyer</em>. Lancez le traitement une fois la connexion retrouvée.
            </dd>

            <dt>Un lot ou une entreprise n’a pas été détecté.</dt>
            <dd>
              Ajoutez-le à la main depuis l’écran de vérification ou plus tard avec le bouton <em>+</em> des lots. Si
              vous recevez un document plus complet, utilisez <strong>Mettre à jour le chantier</strong> : seules les
              informations manquantes seront ajoutées.
            </dd>

            <dt>Mes documents sont sur mon ordinateur.</dt>
            <dd>
              Créez le chantier depuis ce portail (bouton <strong>Créer un projet</strong> du tableau de bord) : le
              fonctionnement est identique et le chantier apparaît ensuite dans l’app.
            </dd>
          </dl>
        </Section>
      </article>
    </div>
  );
}

export default GuidePage;
